import React, { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, IconButton, Box, Typography, Chip, CircularProgress, Alert, Grid, Paper, Stack, LinearProgress } from '@mui/material';
import { Close as CloseIcon, Fingerprint as FingerprintIcon, ShieldOutlined as ShieldIcon } from '@mui/icons-material';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { adminDashboardService } from '../../../services/dashboard';

// PASSKEY, TOTP and IN_APP are the only three authentication methods the
// product offers — the backend never returns anything else.
const METHOD_LABELS = { IN_APP: 'IN-APP' };
const methodLabel = (method) => METHOD_LABELS[method] || method;
const METHOD_COLORS = { PASSKEY: '#2e7d32', IN_APP: '#1565c0', TOTP: '#f9a825' };
const STATUS_COLORS = {
  APPROVED: '#2e7d32',
  TIMEOUT: '#f9a825',
  REJECTED: '#1565c0',
  FAILED: '#c62828',
};
const VISIBLE_STATUSES = ['APPROVED', 'FAILED', 'REJECTED', 'TIMEOUT'];

const ApplicationDrilldownDialog = ({ appId, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!appId) return;
    setLoading(true);
    setError(null);
    adminDashboardService
      .getApplicationDrilldown(appId, '30d')
      .then((res) => setData(res.data))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [appId]);

  const chartData = (data?.authenticationVolume?.series || []).map((p) => ({
    label: new Date(p.date).toLocaleDateString([], { month: 'short', day: 'numeric' }),
    requests: p.requests,
  }));

  const methods = (data?.authenticationMethods || []).filter((m) => METHOD_COLORS[m.method]);
  const statuses = (data?.authenticationStatus || []).filter((s) => VISIBLE_STATUSES.includes(s.status));
  const maxStatusCount = Math.max(1, ...statuses.map((s) => s.count));

  return (
    <Dialog open={Boolean(appId)} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {data?.application?.appName || 'Application analytics'}
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <Box>
            <Chip
              label={data.application.isDeleted ? 'DELETED' : data.application.enabled ? 'ACTIVE' : 'DISABLED'}
              size="small"
              sx={{ mb: 2 }}
            />
            <Typography variant="subtitle2" gutterBottom>
              Authentication volume (30d)
            </Typography>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Area type="monotone" dataKey="requests" stroke="#1565c0" fill="#1565c033" />
              </AreaChart>
            </ResponsiveContainer>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} sm={6}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                  <FingerprintIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Authentication Methods
                  </Typography>
                </Stack>
                {methods.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No data
                  </Typography>
                ) : (
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <Stack spacing={1}>
                      {methods.map((m) => (
                        <Stack key={m.method} direction="row" alignItems="center" spacing={1}>
                          <Box
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              bgcolor: METHOD_COLORS[m.method] || '#757575',
                              flexShrink: 0,
                            }}
                          />
                          <Typography variant="body2" sx={{ flex: 1 }}>
                            {methodLabel(m.method)}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {m.percentage}%
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Paper>
                )}
              </Grid>

              <Grid item xs={12} sm={6}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                  <ShieldIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Authentication Status
                  </Typography>
                </Stack>
                {statuses.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No data
                  </Typography>
                ) : (
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <Stack spacing={1.25}>
                      {statuses.map((s) => (
                        <Box key={s.status}>
                          <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.3 }}>
                            <Typography variant="body2">{s.status}</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {s.count.toLocaleString()}
                            </Typography>
                          </Stack>
                          <LinearProgress
                            variant="determinate"
                            value={(s.count / maxStatusCount) * 100}
                            sx={{
                              height: 6,
                              borderRadius: 3,
                              bgcolor: 'action.hover',
                              '& .MuiLinearProgress-bar': {
                                bgcolor: STATUS_COLORS[s.status] || '#757575',
                                borderRadius: 3,
                              },
                            }}
                          />
                        </Box>
                      ))}
                    </Stack>
                  </Paper>
                )}
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ApplicationDrilldownDialog;
